/* eslint-disable camelcase */
import React, { ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import BooleanInput from '../../input/BooleanInput';

import { FormInputsType } from '../../input/FormType';
import RadioPriceCard from '../../input/RadioPriceCard';
import CheckboxInput from '../../input/CheckboxInput';
import { ApiExtLinkType, DirectusFile, getUrlFromSlug } from '../../helpers/ApiHelper';
import i18n from '../../i18n';
import { getCompleteLocale } from '../../helpers/LangHelper';

type Step3Type = {
  // Step 2
  mtbs: boolean;

  // Step 3
  activityPack: 's' | 'm' | 'l' | 'classique' | null;
  animations_bow: boolean;
  animations_mtb: boolean;
  animations_nature: boolean;
  animations_forest: boolean;
  animations_biathlon: boolean;
  animations_olympiade: boolean;
  animations_jeuDeLOie: boolean;

  visits_electrobroc: boolean;
  visits_maisonDuGruyere: boolean;
  visits_maisonCailler: boolean;
  visits_chateauDeGruyere: boolean;
};

type Step3Props = Step3Type & {
  // eslint-disable-next-line no-unused-vars
  updateFields: (fields: Partial<FormInputsType>) => void;
};

type ExtLinkType = {
  electrobroc: string;
  mdg: string;
  mc: string;
  gruyeres: string;
};

export default function Step3({
  mtbs,
  activityPack,
  animations_bow,
  animations_mtb,
  animations_nature,
  animations_forest,
  animations_biathlon,
  animations_olympiade,
  animations_jeuDeLOie,
  visits_electrobroc,
  visits_maisonDuGruyere,
  visits_maisonCailler,
  visits_chateauDeGruyere,
  updateFields,
}: Step3Props) {
  const { t } = useTranslation('', { keyPrefix: 'Config' });
  const tPacks = useTranslation('', { keyPrefix: 'Packs' }).t;

  // Dynamic Files URLs
  const api = 'https://eterpaz.rdox.ch';
  const [files, setFiles] = useState<DirectusFile[]>();
  const [extLinks, setExtLinks] = useState<ExtLinkType>();

  useEffect(() => {
    try {
      // Get all files
      fetch(`${api}/files`)
        .then((res) => res.json())
        .then((json) => {
          setFiles(json.data);
        });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }

    try {
      // Get ext links
      fetch(`${api}/items/LiensExternes?fields=*,*.*`)
        .then((res) => res.json())
        .then((json) => {
          setExtLinks(
            json.data.links.find(
              (link: ApiExtLinkType) =>
                link.languages_code === getCompleteLocale(i18n.resolvedLanguage)
            )
          );
        });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }
  }, [i18n.resolvedLanguage]);

  const cardOptions: {
    value: string;
    title: string;
    subtitle: string;
    surtitle?: string;
    price: number;
    priceText?: string;
    body: string | ReactElement;
  }[] = [
    {
      value: 'classique',
      surtitle: ' ',
      title: `${t('packs.classique.title')} »`,
      subtitle: t('packs.classique.subtitle'),
      priceText: t('packs.classique.priceText') || '',
      price: 52,
      body: (
        <>
          <b>{t('packs.classique.s1title')}</b>
          <p>{t('packs.classique.s1desc')}</p>
          <hr />
          <b>{t('packs.classique.s2title')}</b>
          <p>{t('packs.classique.s2desc')}</p>
          <small>{t('packs.classique.s2sm')}</small>
          <hr />
          <b>{t('packs.classique.s3title')}</b>
          <p>{t('packs.classique.s3desc')}</p>
        </>
      ),
    },
    {
      value: 'l',
      surtitle: `${tPacks('activities.2.surname')}${mtbs ? '+' : ''}`,
      title: tPacks('activities.2.name'),
      subtitle: t('packs.l.subtitle'),
      priceText: `${t('packs.l.priceText')}${mtbs ? `, ${t('mtbs.withMtb')}` : ''}`,
      price: mtbs ? 27 : 20,
      body: (
        <>
          <b>{`${t('packs.m.title')}${mtbs ? '+' : ''} » ${t('packs.m.priceText')}`}</b>
          <hr />
          <b>{t('packs.l.s1title')}</b>
          <p>{t('packs.l.s1desc')}</p>
          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.l.s1slug`), files)?.id}`}
            target="_blank"
          >
            {t('packs.l.s1link')}
          </Link>
          <hr />
          <b>{t('packs.l.s2title')}</b>
          <p>{t('packs.l.s2desc')}</p>
          <hr />
          <b>{t('packs.l.s3title')}</b>
          <p>{t('packs.l.s3desc')}</p>
        </>
      ),
    },

    {
      value: 'm',
      surtitle: `${tPacks('activities.1.surname')}${mtbs ? '+' : ''}`,

      title: tPacks('activities.1.name'),
      subtitle: t('packs.m.subtitle'),
      priceText: `${t('packs.m.priceText')}${mtbs ? `, ${t('mtbs.withMtb')}` : ''}`,
      price: mtbs ? 22 : 15,
      body: (
        <>
          <b>{`${t('packs.s.title')}${mtbs ? '+' : ''} » ${t('packs.s.priceText')}`}</b>
          <hr />
          <b>{t('packs.m.s1title')}</b>
          <p>{t('packs.m.s1desc')}</p>
          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.m.s1slug`), files)?.id}`}
            target="_blank"
          >
            {t('packs.m.s1link')}
          </Link>
          <hr />
          <p>
            <b>{t('packs.m.s21title')}</b>
            <br />
            {t('packs.m.s21desc')}
          </p>
          <p>
            <b>{t('packs.m.s22title')}</b>
            <br />
            {t('packs.m.s22desc')}
          </p>
          <p>
            <b>{t('packs.m.s23title')}</b>
            <br />
            {t('packs.m.s23desc')}
          </p>
          <hr />
          <b>{t('packs.m.s3title')}</b>
          <p>{t('packs.m.s3desc')}</p>
        </>
      ),
    },
    {
      value: 's',
      surtitle: `${tPacks('activities.0.surname')}${mtbs ? '+' : ''}`,
      title: tPacks('activities.0.name'),
      subtitle: t('packs.s.subtitle'),
      priceText: `${t('packs.s.priceText')}${mtbs ? `, ${t('mtbs.withMtb')}` : ''}`,
      price: mtbs ? 15 : 8,
      body: (
        <>
          <b>{t('packs.s.s1title')}</b>
          <p>{t('packs.s.s1desc')}</p>

          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s1slug`), files)?.id}`}
            target="_blank"
          >
            {t('inventory')}
          </Link>
          <hr />
          <b>{t('packs.s.s2title')}</b>
          <p>{t('packs.s.s2desc')}</p>
          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s2slug`), files)?.id}`}
            target="_blank"
          >
            {t('inventory')}
          </Link>
          <hr />
          <b>{t('packs.s.s3title')}</b>
          <p>{t('packs.s.s3desc')}</p>
          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s3slug`), files)?.id}`}
            target="_blank"
          >
            {t('inventory')}
          </Link>
          <hr />
          <span className={`${mtbs || 'notAvailable'}`}>
            <b>{t('packs.s.s4title')}</b>
            <p>{t('packs.s.s4desc')}</p>
            <Link
              to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s4slug`), files)?.id}`}
              target="_blank"
            >
              {t('inventory')}
            </Link>
          </span>
        </>
      ),
    },
  ];

  const uncheckAllFields: Partial<FormInputsType> = {
    animations_bow: false,
    animations_mtb: false,
    animations_nature: false,
    animations_forest: false,
    animations_biathlon: false,
    animations_olympiade: false,
    animations_jeuDeLOie: false,
    visits_electrobroc: false,
    visits_maisonDuGruyere: false,
    visits_maisonCailler: false,
    visits_chateauDeGruyere: false,
    // visits_chateauDeGruyereDetails: 'simple',
  };

  const classicFields: Partial<FormInputsType> = {
    animations_bow: false,
    animations_mtb: false,
    animations_nature: false,
    animations_forest: false,
    animations_biathlon: false,
    animations_olympiade: false,
    animations_jeuDeLOie: false,
    visits_electrobroc: true,
    visits_maisonDuGruyere: false,
    visits_maisonCailler: true,
    visits_chateauDeGruyere: false,
    // visits_chateauDeGruyereDetails: 'simple',
  };

  const updatePacks = (fields: Partial<FormInputsType>) => {
    switch (fields.activityPack) {
      case 'l':
        updateFields(uncheckAllFields);
        break;

      case 'classique':
        updateFields(classicFields);
        break;

      default:
        break;
    }
    updateFields(fields);
  };

  return (
    <div id="step3">
      <section className="configuratorStep">
        <article className="configuratorPacks">
          <div className="titles">
            <h3 className="centeredDashed">{t('step3.title')}</h3>
            <p>{t('step3.desc')}</p>
          </div>
          <div className="carouselHolder">
            <RadioPriceCard
              name="activityPack"
              title=""
              addClass="activities"
              options={cardOptions}
              selectedOption={activityPack || 'l'}
              updateSingleField={updatePacks}
            />
          </div>
        </article>
        <article className="container tight">
          <h3 className="centeredDashed">{t('mtbs.title')}</h3>
          {activityPack === 'classique' ? (
            <p>{t('mtbs.includedInClassic')}</p>
          ) : (
            <>
              <p>{t('mtbs.desc')}</p>
              <BooleanInput
                name="mtbs"
                title={t('mtbs.label')}
                initValue={mtbs}
                updateSingleField={updateFields}
              />
            </>
          )}
          <small>{t('mtbs.small')}</small>
          <Link
            to={`${api}/assets/${getUrlFromSlug(t(`packs.s.s4slug`), files)?.id}`}
            target="_blank"
          >
            {t('mtbs.link')}
          </Link>
        </article>
      </section>
      <section className="configuratorStep container">
        <article>
          <div className="titles">
            <h3 className="centeredDashed">{t('animations.title')}</h3>
            {activityPack === 'l' && <p>{t('animations.desc')}</p>}
            {activityPack === 'classique' && <p>{t('animations.descPackClassique')}</p>}
            {(activityPack === 's' || activityPack === 'm') && (
              <p className="minNightsWarning">{t('animations.descPackSM')}</p>
            )}
          </div>
          {activityPack !== 'classique' && (
            <div className="checkboxes animations">
              <legend>{t('animations.sportsAnimations')}</legend>
              <CheckboxInput
                title={t('animations.bow')}
                name="animations_bow"
                checked={animations_bow}
                disabled={activityPack !== 'l'}
                updateSingleField={updateFields}
              />
              <div>
                <CheckboxInput
                  title={t('animations.mtb')}
                  name="animations_mtb"
                  checked={animations_mtb}
                  disabled={activityPack !== 'l'}
                  updateSingleField={updateFields}
                />
                <small className="xs">{t('animations.mtbSmall')}</small>
              </div>
              <legend>{t('animations.natureAnimations')}</legend>
              <CheckboxInput
                title={t('animations.nature')}
                name="animations_nature"
                checked={animations_nature}
                disabled={activityPack !== 'l'}
                updateSingleField={updateFields}
              />
              <CheckboxInput
                title={t('animations.forest')}
                name="animations_forest"
                checked={animations_forest}
                disabled={activityPack !== 'l'}
                updateSingleField={updateFields}
              />
              <small>
                <Link
                  to={`${api}/assets/${
                    getUrlFromSlug(t(`animations.natureConditionsSlug`), files)?.id
                  }`}
                  target="_blank"
                >
                  {t('animations.natureConditions')}
                </Link>
              </small>
              <legend>{t('animations.groupAnimations')}</legend>
              <CheckboxInput
                title={t('animations.biathlon')}
                name="animations_biathlon"
                checked={animations_biathlon}
                disabled={activityPack !== 'l'}
                updateSingleField={updateFields}
              />
              <CheckboxInput
                title={t('animations.olympiade')}
                name="animations_olympiade"
                checked={animations_olympiade}
                disabled={activityPack !== 'l'}
                updateSingleField={updateFields}
              />
              <CheckboxInput
                title={t('animations.jeuDeLOie')}
                name="animations_jeuDeLOie"
                checked={animations_jeuDeLOie}
                disabled={activityPack !== 'l'}
                updateSingleField={updateFields}
              />
              <small>
                <Link
                  to={`${api}/assets/${
                    getUrlFromSlug(t(`animations.groupConditionsSlug`), files)?.id
                  }`}
                  target="_blank"
                >
                  {t('animations.groupConditions')}
                </Link>
              </small>
            </div>
          )}
        </article>
      </section>
      <section className="container medTight">
        <article>
          <div className="titles">
            <h3 className="centeredDashed">{t('visits.title')}</h3>

            {activityPack === 'l' && <p>{t('visits.desc')}</p>}
            {activityPack === 'classique' && <p>{t('visits.descPackClassique')}</p>}
            {(activityPack === 's' || activityPack === 'm') && (
              <p className="minNightsWarning">{t('visits.descPackSM')}</p>
            )}
          </div>

          {
            /* activityPack !== 'classique' && ( */
            <div className="checkboxes visits">
              <CheckboxInput
                title={`${t('visits.electrobroc')}`}
                name="visits_electrobroc"
                checked={visits_electrobroc}
                disabled={activityPack !== 'l'}
                extLink={extLinks?.electrobroc || 'https://www.electrobroc.ch/'}
                updateSingleField={updateFields}
              />

              <CheckboxInput
                title={`${t('visits.mc')}`}
                name="visits_maisonCailler"
                checked={visits_maisonCailler}
                disabled={activityPack !== 'l'}
                extLink={extLinks?.mc || 'https://cailler.ch/fr/experiences-cailler'}
                updateSingleField={updateFields}
              />
              {activityPack === 'classique' && visits_chateauDeGruyere ? (
                ''
              ) : (
                <CheckboxInput
                  title={`${t('visits.mdg')}`}
                  name="visits_maisonDuGruyere"
                  checked={visits_maisonDuGruyere}
                  disabled={activityPack === 's' || activityPack === 'm'}
                  extLink={extLinks?.mdg || 'https://www.lamaisondugruyere.ch/'}
                  updateSingleField={updateFields}
                />
              )}

              {activityPack === 'classique' && visits_maisonDuGruyere ? (
                ''
              ) : (
                <CheckboxInput
                  title={t('visits.gruyeres')}
                  name="visits_chateauDeGruyere"
                  checked={visits_chateauDeGruyere}
                  disabled={activityPack === 's' || activityPack === 'm'}
                  extLink={extLinks?.gruyeres || 'https://www.chateau-gruyeres.ch/'}
                  updateSingleField={updateFields}
                />
              )}
              <small className="xs">{t('visits.price')}</small>
            </div>
            /* ) */
          }
        </article>
      </section>
    </div>
  );
}
